import { DEFAULT_TEXT_SETTINGS, DEFAULT_BACKGROUND_SETTINGS, DEFAULT_AUDIO_SETTINGS, DEFAULT_THEME_SETTINGS, DEFAULT_SECTION_SETTINGS, DEFAULT_CURSOR_SETTINGS, DEFAULT_YOUTUBE_EMBED_SETTINGS, DEFAULT_TWITCH_EMBED_SETTINGS, DEFAULT_SPOTIFY_EMBED_SETTINGS, DEFAULT_COMMENT_SETTINGS, DEFAULT_WIDGET_SETTINGS, type TextSettings, type BackgroundSettings, type AudioSettings, type ThemeSettings, type SectionSettings, type CursorSettings, type YouTubeEmbedSettings, type TwitchEmbedSettings, type SpotifyEmbedSettings, type CommentSettings, type WidgetSettings } from '~/stores/auth'
import type { LinkItem } from '~/stores/links'
import type { CharacterAchievementItem } from '~/stores/achievements'
import type { SetupItem } from '~/stores/setups'

export interface CharacterBlockData {
  reason: string
  blocked_at: string
  appeal_submitted_at: string | null
}

export interface CharacterPageData {
  id: string
  uid: number
  username: string
  views: number
  totalViews: number
  block: CharacterBlockData | null
  discord_user_id: string | null
  youtube_channel_id: string | null
  twitch_user_id: string | null
  spotify_user_id: string | null
  plan: string
  customDomain: string | null
}

export interface CharacterPageSettings {
  display_name: string | null
  bio: string | null
  location: string | null
  pronouns: string | null
  avatar_id: string | null
  avatar_url: string | null
  background_id: string | null
  background_url: string | null
  background_type: 'image' | 'video'
  cursor_id: string | null
  cursor_url: string | null
  layout: string
  text: TextSettings
  background: BackgroundSettings
  audio: AudioSettings
  theme: ThemeSettings
  sections: SectionSettings
  cursor: CursorSettings
  widgets: WidgetSettings
  comments: CommentSettings
  youtube: YouTubeEmbedSettings
  twitch: TwitchEmbedSettings
  spotify: SpotifyEmbedSettings
  branding?: boolean
  custom_font_url: string | null
  custom_font_id: string | null
  setup_photo_url: string | null
  displayed_friends: string[]
}

export interface AudioPlaylistItem {
  id: string
  original_name: string
  url: string | null
  position: number
}

export interface ClipDisplayItem {
  id: string
  url: string | null
  title: string | null
  position: number
}

export interface FriendDisplayItem {
  character_id: string
  username: string
  avatar_url: string | null
  /** Friend's active custom domain - friend links prefer it over mychar.gg */
  custom_domain?: string | null
}

interface CharacterApiResponse {
  character: {
    id: string
    uid: number
    username: string | null
    views: number
    block: CharacterBlockData | null
    discord_user_id?: string | null
    youtube_channel_id?: string | null
    twitch_user_id?: string | null
    spotify_user_id?: string | null
    onboarding_completed: boolean
    created_at: string
    updated_at: string
    plan?: string
    customDomain?: string | null
  }
  settings: {
    display_name: string | null
    bio: string | null
    location: string | null
    pronouns: string | null
    avatar_id: string | null
    avatar_url: string | null
    background_id: string | null
    background_url: string | null
    background_type?: 'image' | 'video'
    cursor_id: string | null
    cursor_url: string | null
    layout: string
    text: Record<string, unknown> | null
    background: Record<string, unknown> | null
    audio: Record<string, unknown> | null
    theme: Record<string, unknown> | null
    sections?: Record<string, unknown> | null
    cursor?: Record<string, unknown> | null
    youtube?: Record<string, unknown> | null
    twitch?: Record<string, unknown> | null
    spotify?: Record<string, unknown> | null
    branding?: boolean
    custom_font_url?: string | null
    custom_font_id?: string | null
    setup_photo_url?: string | null
  }
  audioPlaylist: AudioPlaylistItem[]
  links: LinkItem[]
  achievements: CharacterAchievementItem[]
  clips: ClipDisplayItem[]
  setupItems: SetupItem[]
  friends: FriendDisplayItem[]
  totalViews: number
  viewer?: { is_owner: boolean, is_staff: boolean }
}

function mergeTextSettings(raw: Record<string, unknown> | null | undefined): TextSettings {
  return {
    ...DEFAULT_TEXT_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeBackgroundSettings(raw: Record<string, unknown> | null | undefined): BackgroundSettings {
  return {
    ...DEFAULT_BACKGROUND_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeAudioSettings(raw: Record<string, unknown> | null | undefined): AudioSettings {
  return {
    ...DEFAULT_AUDIO_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeThemeSettings(raw: Record<string, unknown> | null | undefined): ThemeSettings {
  return {
    ...DEFAULT_THEME_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeSectionSettings(raw: Record<string, unknown> | null | undefined): SectionSettings {
  return {
    ...DEFAULT_SECTION_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeCursorSettings(raw: Record<string, unknown> | null | undefined): CursorSettings {
  return {
    ...DEFAULT_CURSOR_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeYouTubeEmbedSettings(raw: Record<string, unknown> | null | undefined): YouTubeEmbedSettings {
  return {
    ...DEFAULT_YOUTUBE_EMBED_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeTwitchEmbedSettings(raw: Record<string, unknown> | null | undefined): TwitchEmbedSettings {
  return {
    ...DEFAULT_TWITCH_EMBED_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeSpotifyEmbedSettings(raw: Record<string, unknown> | null | undefined): SpotifyEmbedSettings {
  return {
    ...DEFAULT_SPOTIFY_EMBED_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeCommentSettings(raw: Record<string, unknown> | null | undefined): CommentSettings {
  return {
    ...DEFAULT_COMMENT_SETTINGS,
    ...(raw ?? {})
  }
}

function mergeWidgetSettings(raw: Record<string, unknown> | null | undefined): WidgetSettings {
  const r = (raw ?? {}) as Record<string, Record<string, unknown>>
  return {
    audio: { ...DEFAULT_WIDGET_SETTINGS.audio, ...(r.audio ?? {}) },
    views: { ...DEFAULT_WIDGET_SETTINGS.views, ...(r.views ?? {}) },
    comments: { ...DEFAULT_WIDGET_SETTINGS.comments, ...(r.comments ?? {}) }
  }
}

export function useCharacterPage(username: string | Ref<string>) {
  const usernameRef = toRef(username)

  // useRequestFetch forwards the incoming request's cookies during SSR so the
  // API resolves the logged-in viewer (owner/staff). Bare $fetch would render
  // the owner as anonymous on hard refresh, breaking the blocked-page 410 gate.
  const requestFetch = useRequestFetch()
  const asyncData = useAsyncData(
    `character-page-${toValue(usernameRef)}`,
    () => requestFetch<CharacterApiResponse>(`/api/character/${toValue(usernameRef)}`)
  )

  const { data, status, error, refresh } = asyncData

  const character = computed<CharacterPageData | null>(() => {
    if (!data.value) return null
    const c = data.value.character
    return {
      id: c.id,
      uid: c.uid,
      username: c.username!,
      views: c.views,
      totalViews: data.value.totalViews ?? 0,
      block: c.block,
      discord_user_id: c.discord_user_id ?? null,
      youtube_channel_id: c.youtube_channel_id ?? null,
      plan: c.plan ?? 'free',
      twitch_user_id: c.twitch_user_id ?? null,
      spotify_user_id: c.spotify_user_id ?? null,
      customDomain: c.customDomain ?? null
    }
  })

  const settings = computed<CharacterPageSettings>(() => {
    const raw = data.value?.settings
    return {
      display_name: raw?.display_name ?? null,
      bio: raw?.bio ?? null,
      location: raw?.location ?? null,
      pronouns: raw?.pronouns ?? null,
      avatar_id: raw?.avatar_id ?? null,
      avatar_url: raw?.avatar_url ?? null,
      background_id: raw?.background_id ?? null,
      background_url: raw?.background_url ?? null,
      background_type: (raw?.background_type as 'image' | 'video') ?? 'image',
      cursor_id: raw?.cursor_id ?? null,
      cursor_url: raw?.cursor_url ?? null,
      layout: raw?.layout ?? 'default',
      text: mergeTextSettings(raw?.text as Record<string, unknown> | null),
      background: mergeBackgroundSettings(raw?.background as Record<string, unknown> | null),
      audio: mergeAudioSettings(raw?.audio as Record<string, unknown> | null),
      theme: mergeThemeSettings(raw?.theme as Record<string, unknown> | null),
      sections: mergeSectionSettings(raw?.sections as Record<string, unknown> | null),
      cursor: mergeCursorSettings(raw?.cursor as Record<string, unknown> | null),
      youtube: mergeYouTubeEmbedSettings(raw?.youtube as Record<string, unknown> | null),
      twitch: mergeTwitchEmbedSettings(raw?.twitch as Record<string, unknown> | null),
      widgets: mergeWidgetSettings(raw?.widgets as Record<string, unknown> | null),
      comments: mergeCommentSettings(raw?.comments as Record<string, unknown> | null),
      spotify: mergeSpotifyEmbedSettings(raw?.spotify as Record<string, unknown> | null),
      branding: raw?.branding ?? true,
      custom_font_url: raw?.custom_font_url ?? null,
      custom_font_id: raw?.custom_font_id ?? null,
      setup_photo_url: raw?.setup_photo_url ?? null,
      displayed_friends: (raw as any)?.displayed_friends ?? []
    }
  })

  const audioPlaylist = computed<AudioPlaylistItem[]>(() => {
    return data.value?.audioPlaylist ?? []
  })

  const links = computed<LinkItem[]>(() => {
    return data.value?.links ?? []
  })

  const achievements = computed<CharacterAchievementItem[]>(() => {
    return data.value?.achievements ?? []
  })

  const clips = computed<ClipDisplayItem[]>(() => {
    return data.value?.clips ?? []
  })

  const setupItems = computed<SetupItem[]>(() => {
    return data.value?.setupItems ?? []
  })

  const friends = computed<FriendDisplayItem[]>(() => {
    return data.value?.friends ?? []
  })

  const isLoading = computed(() => status.value === 'pending')

  const viewer = computed(() => data.value?.viewer ?? { is_owner: false, is_staff: false })

  return {
    character,
    settings,
    audioPlaylist,
    links,
    achievements,
    clips,
    setupItems,
    friends,
    isLoading,
    error,
    refresh,
    asyncData,
    viewer
  }
}
